import { debug } from './utils/env';
import { eventBus } from 'EventBus/';
import { inViewport } from './utils/inViewport';
import { isVisible } from './utils/isVisible';
import { throttle } from '../../utils/timing';
import { AnalyticsHandler } from './AnalyticsHandler';
import createDataObjectBasedOnDom from './createDataObjectBasedOnDom';

if (
  'IntersectionObserver' in window &&
  'IntersectionObserverEntry' in window &&
  'intersectionRatio' in window.IntersectionObserverEntry.prototype &&
  !('isIntersecting' in IntersectionObserverEntry.prototype)
) {
  Object.defineProperty(
    window.IntersectionObserverEntry.prototype,
    'isIntersecting',
    {
      get() {
        return this.intersectionRatio > 0;
      },
    }
  );
}

/**
 * Create event callback with data processing and error handling
 */
function createEventCallback(node, data, modifiers) {
  const isCurrentTarget = ({ target, currentTarget }) =>
    target === currentTarget;

  return (registerOnly = false, event) => {
    if (modifiers.target && event && !isCurrentTarget(event)) {
      return;
    }

    let dataToSend = modifiers.dom
      ? createDataObjectBasedOnDom(node.closest('[data-tag-wrapper]'), data)
      : data;

    if (modifiers.view) {
      dataToSend.viewPort = true;
    }

    try {
      AnalyticsHandler.getAnalyticsHandler().push(dataToSend, registerOnly);
    } catch (er) {
      if (debug) {
        node.setAttribute('data-error', er.message);
        node.classList.add('accessibilityError');
      }
      throw er;
    }
  };
}

/**
 * Handle click event type
 */
function handleClickEvent(node, onEvent) {
  node.addEventListener('click', (event) => {
    onEvent(false, event);
  });
}

/**
 * Handle conditional event type
 */
function handleConditionalEvent(onEvent) {
  onEvent(true);
  eventBus.$on('conditions-met', onEvent);
}

/**
 * Handle impression with IntersectionObserver
 */
function handleIntersectionObserver(node, onEvent) {
  const observer = new IntersectionObserver(
    (changes) => {
      changes.forEach((change) => {
        if (change.isIntersecting) {
          onEvent();
          observer.unobserve(node);
        }
      });
    },
    {
      threshold: [0.006],
    }
  );

  observer.observe(node);
}

/**
 * Handle impression with scroll fallback
 */
function handleScrollFallback(node, onEvent) {
  const scrollCallback = (event) => {
    /* istanbul ignore else */
    if (inViewport(node) && isVisible(node)) {
      onEvent(false, event);
      window.removeEventListener('scroll', throttle(scrollCallback, 500));
    }
  };

  window.addEventListener('scroll', throttle(scrollCallback, 500), false);
  scrollCallback();
}

/**
 * Binds the event for the Google TagManager action to the DOM node. Click or Impression.
 * @param {String} type Type of event. Can be click or impression.
 * @param {Node} node Dom node to use for event handling.
 * @param {Object} data Data send by the component who wants to send an analytics event.
 * @param {Object} modifiers Modifiers passed from directive
 */
export default function handleEvent(type, node, data, modifiers = {}) {
  const onEvent = createEventCallback(node, data, modifiers);

  if (type === 'click') {
    handleClickEvent(node, onEvent);
  } else if (type === 'conditional') {
    handleConditionalEvent(onEvent);
  } else if ('IntersectionObserver' in window) {
    handleIntersectionObserver(node, onEvent);
  } else {
    handleScrollFallback(node, onEvent);
  }
}
